/**
 * GlobalNotifications — canonical shell-level feedback renderer.
 *
 * The fixed stack owns positioning; individual alerts remain in normal flow so
 * multiple notifications never overlap. Success/info/warning announcements are
 * polite, while errors remain assertive.
 */
import { useEffect } from 'react';
import { Alert, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import {
  notificationsReadAtom,
  dismissNotificationAtom,
  type Notification,
} from '../../features/notifications/notification-store';

const MAX_VISIBLE = 4;

function NotificationItem({ notification }: { notification: Notification }) {
  const [, dismiss] = useAtom(dismissNotificationAtom);

  useEffect(() => {
    if (notification.duration === 0) return;
    const timer = window.setTimeout(() => dismiss(notification.id), notification.duration);
    return () => window.clearTimeout(timer);
  }, [notification.id, notification.duration, dismiss]);

  return (
    <Alert
      severity={notification.severity}
      onClose={() => dismiss(notification.id)}
      variant="filled"
      role={notification.severity === 'error' ? 'alert' : 'status'}
      data-testid={`notification-${notification.id}`}
      sx={{
        width: '100%',
        boxShadow: 3,
        alignItems: 'center',
      }}
    >
      {notification.message}
    </Alert>
  );
}

export function GlobalNotifications() {
  const [notifications] = useAtom(notificationsReadAtom);
  const visible = notifications.slice(-MAX_VISIBLE).reverse();

  if (visible.length === 0) return null;

  return (
    <Stack
      spacing={1}
      sx={{
        position: 'fixed',
        left: { xs: 2, sm: 'auto' },
        right: { xs: 2, sm: 3 },
        bottom: 'max(16px, env(safe-area-inset-bottom))',
        width: { xs: 'auto', sm: 'min(420px, calc(100vw - 48px))' },
        zIndex: theme => theme.zIndex.snackbar,
        pointerEvents: 'none',
        '& > *': { pointerEvents: 'auto' },
      }}
      data-testid="global-notifications"
    >
      {visible.map(notification => (
        <NotificationItem key={notification.id} notification={notification} />
      ))}
    </Stack>
  );
}
